<script lang="ts" setup>
import { computed, onMounted, watch } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { CategoryType } from '@libs/trip-planner-interfaces';
import type {
  AutoCompleteSuggestion,
  AutoCompleteSuggestionsByCategory,
  Coordinate,
  FerryAutoCompleteSuggestion,
  FlightAutoCompleteSuggestion,
} from '@libs/trip-planner-interfaces';
import { AppInputAutosuggestDropdown } from '@libs/ui';

import { useAutoCompleteStore } from './stores/autoCompleteStore';
import type { AutoCompleteType } from './AutoComplete.model';
import { handleSelectedSuggestion } from './AutoComplete.helper';

const autoCompleteStore = useAutoCompleteStore();
const { t } = i18n.global;

interface AutoCompleteSuggestionsProps {
  autoCompleteType: AutoCompleteType;
  initialSearchText: string;
  placeholder?: string;
  filterByCategoryType?: CategoryType[];
  initialUserPosition?: Coordinate;
  showBackButton?: boolean;
  disable?: boolean;
  shouldFocusOnInit?: boolean;
  wrapperBottomBoundary?: number;
  headerIcon?: boolean;
}

const props = defineProps<AutoCompleteSuggestionsProps>();

interface AutoCompleteSuggestionsEmits {
  (
    eventName: 'airport-selected',
    airportSuggestion: FlightAutoCompleteSuggestion
  ): void;
  (eventName: 'select-ferry-route', suggestion: FerryAutoCompleteSuggestion);
  (eventName: 'select-suggestion', suggestion: AutoCompleteSuggestion): void;
  (eventName: 'close'): void;
  (eventName: 'on-input-click'): void;
}

const emit = defineEmits<AutoCompleteSuggestionsEmits>();

function updateModelValue(value: string): void {
  autoCompleteStore.searchText = value;
  autoCompleteStore.setSearchText(value);
}

watch(
  () => props.initialUserPosition,
  (position) => {
    if (position) {
      autoCompleteStore.setSearchPosition(position);
    }
  },
  { immediate: true }
);

function handleSelect(suggestion: AutoCompleteSuggestion): void {
  handleSelectedSuggestion(suggestion, {
    onAirportSelected: (airportSuggestion) =>
      emit('airport-selected', airportSuggestion),
    onFerryRouteSelected: (ferrySuggestion) =>
      emit('select-ferry-route', ferrySuggestion),
    onGenericSuggestionSelected: (genericSuggestion) =>
      emit('select-suggestion', genericSuggestion),
  });
}

function handleClose(): void {
  autoCompleteStore.cleanAutoCompleteSuggestions();
  emit('close');
}

function handleOpen(ifInputFocused: boolean): void {
  if (ifInputFocused) {
    autoCompleteStore.initializeAutoComplete(
      props.autoCompleteType,
      props.initialSearchText
    );
  }
  emit('on-input-click');
}

const filteredSuggestions = computed(() => {
  const originalSuggestions = autoCompleteStore.autoCompleteSuggestions;

  if (props.filterByCategoryType?.includes(CategoryType.GeolocationPosition)) {
    const myLocationCategory: AutoCompleteSuggestionsByCategory = {
      label: t('planner.location'),
      type: CategoryType.GeolocationPosition,
      data: [
        {
          label: t('planner.my_position'),
          category: 'Geolocation',
          categoryType: CategoryType.GeolocationPosition,
          isCurrentPosition: true,
        },
      ],
    };

    return [myLocationCategory, ...originalSuggestions];
  }

  return originalSuggestions;
});

onMounted(() => {
  autoCompleteStore.cleanAutoCompleteSuggestions();
});
</script>

<template>
  <AppInputAutosuggestDropdown
    :suggestions="filteredSuggestions"
    :filter-by-category-type="props.filterByCategoryType"
    :input-text="props.initialSearchText"
    :placeholder="
      props.placeholder || autoCompleteStore.autoCompletePlaceholder
    "
    :max-visible-items="5"
    theme="neutral"
    :disable="props.disable"
    highlight
    :should-focus-on-init="props.shouldFocusOnInit"
    :wrapper-bottom-boundary="wrapperBottomBoundary"
    :header-icon="props.headerIcon"
    @update-model-value="updateModelValue"
    @select="handleSelect"
    @close="handleClose"
    @on-input-focus="handleOpen"
  >
    <template #left-icon>
      <slot name="left-icon" />
    </template>
  </AppInputAutosuggestDropdown>
</template>
